// src/crypto/e2ee.js
//
// 🟢 PER-DEVICE E2EE — the browser twin of the mobile CryptoService text path.
// Byte-compatible with services/CryptoService.ts: same nacl.box (Curve25519 +
// XSalsa20-Poly1305), same base64 (tweetnacl-util), same message shape
// ({ perDeviceKeys[deviceId]:{text,nonce}, senderDeviceId }). A message sealed by
// the phone decrypts here and vice-versa.

import nacl from 'tweetnacl';
import util from 'tweetnacl-util';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { getIdentity, ensureKeyPublished } from './identity';
import { needsPreview, previewText } from './textPreview'; // 🔒 a long text's notification preview
import { sealSkips } from '../services/sealDevices';   // 🧳 which devices a NEW message is sealed for

/* 🧳 THE DEVICES A NEW MESSAGE IS SEALED FOR: the fresh directory, minus the ones known to be
      gone (a browser that logged out, one idle past the mailbox's line, a phone with no push
      token and no sign of life for months — ../services/sealDevices). This browser always. */
export async function sealKeysFor(userId, myDeviceId) {
  const keys = await getDeviceKeys(userId, true);
  if (keys === null) return null;
  const skip = (_cache.get(userId)?.skip || []).filter((id) => id !== myDeviceId);
  if (!skip.length) return keys;
  const out = {};
  for (const id of Object.keys(keys)) if (!skip.includes(id)) out[id] = keys[id];
  return Object.keys(out).length ? out : keys;
}

// ── device-key directory (with cache, matches mobile's getDeviceKeysForUser) ──
const _cache = new Map(); // userId -> { keys:{deviceId:pubB64}, ts }
const TTL = 60 * 1000;

export async function getDeviceKeys(userId, forceRefresh = false) {
  const hit = _cache.get(userId);
  if (!forceRefresh && hit && Date.now() - hit.ts < TTL) return hit.keys;
  try {
    const snap = await getDoc(doc(db, 'users', userId));
    const devices = snap.data()?.devices || {};
    const keys = {};
    for (const d of Object.keys(devices)) {
      const pk = devices[d]?.publicKey;
      if (pk) keys[d] = pk;
    }
    // Legacy single-key fallback so 1:1 with a not-yet-migrated peer still works.
    const legacy = snap.data()?.publicKey;
    if (legacy && !Object.keys(keys).length) keys[`legacy_${userId}`] = legacy;
    /* 🧳 every key stays in `keys` (opening needs the SENDER's, retired or not); `skip` is only
          what a NEW seal leaves out — ../services/sealDevices */
    _cache.set(userId, { keys, ts: Date.now(), skip: sealSkips(devices, Date.now()) });
    return keys;
  } catch (e) {
    // 🟥 null, NOT {}. An empty object is a claim — "this user has no devices" — and the
    //    sealer used to act on it by silently omitting that person from the message.
    //    A failed read is not a fact about the user; it's a fact about the network, and
    //    the two must not look the same to the caller.
    console.warn('[e2ee] device-key read failed for', userId, e?.message);
    return null;
  }
}

/* ══════════════════════════════════════════════════════════════════════════
   DEVICE-ID COLLISION ACROSS ACCOUNTS — ported from CryptoService.buildSealMap
   ══════════════════════════════════════════════════════════════════════════
   🟥 `deviceId` identifies a PHONE, not a phone-plus-account. It survives logout by
   design, so a handset that has been signed into two accounts appears under the SAME
   deviceId in BOTH users' device maps — with a different public key in each.

   perDeviceKeys is keyed by deviceId, and this file used to merge every participant's
   devices with object spread: `allDevices = { ...allDevices, ...keys }`. Later simply
   won. One of the two accounts lost its copy and could never open the message.

   That is exactly why it broke between two SPECIFIC people and nobody else, for every
   message type at once, while the same conversation from the phone was fine — the phone
   fixed this months ago and the browser never got the fix. Whichever account loses the
   spread receives a copy addressed to its own deviceId but sealed to the OTHER account's
   public key, so the reader finds a copy, fails to open it, and marks it terminal:
   "🔒 Encrypted Message", permanently, for that one contact.

   Fix, byte-identical to the phone's: when a deviceId is claimed by more than one
   account, key those entries `uid:deviceId` so both survive. Non-colliding devices keep
   the plain key, so the wire format is unchanged in the common case and older clients
   are unaffected. */
export function buildSealMap(perUser) {
  const owners = new Map();
  const seenUid = new Set();
  for (const entry of perUser || []) {
    if (!entry?.uid || seenUid.has(entry.uid)) continue;   // dedupe repeated participants
    seenUid.add(entry.uid);
    for (const [deviceId, pub] of Object.entries(entry.devices || {})) {
      if (!deviceId || !pub) continue;
      if (!owners.has(deviceId)) owners.set(deviceId, []);
      owners.get(deviceId).push({ uid: entry.uid, pub });
    }
  }
  const out = {};
  let collisions = 0;
  for (const [deviceId, list] of owners) {
    if (list.length === 1) { out[deviceId] = list[0].pub; continue; }
    collisions += 1;
    for (const e of list) out[`${e.uid}:${deviceId}`] = e.pub;
  }
  if (collisions > 0) {
    console.warn(`[e2ee] ${collisions} device id(s) claimed by MULTIPLE accounts — namespaced as uid:deviceId so every account keeps its own copy`);
  }
  return out;
}

/**
 * OUR copy out of a per-device map. Prefer the namespaced entry, fall back to the plain
 * one — the same order the phone reads in (CryptoService.ts:818), so a map sealed by
 * either client opens on either client.
 */
export function myCopyOf(map, myDeviceId) {
  if (!map) return null;
  const uid = auth.currentUser?.uid;
  return (uid && map[`${uid}:${myDeviceId}`]) || map[myDeviceId] || null;
}

// ── primitives ──
export function encryptText(text, recipientPubB64, mySecretU8) {
  const nonce = nacl.randomBytes(nacl.box.nonceLength);
  const box = nacl.box(
    util.decodeUTF8(text),
    nonce,
    util.decodeBase64(recipientPubB64),
    mySecretU8
  );
  if (!box) return null;
  return { text: util.encodeBase64(box), nonce: util.encodeBase64(nonce) };
}

export function decryptText(cipherB64, nonceB64, senderPubB64, mySecretU8) {
  try {
    const opened = nacl.box.open(
      util.decodeBase64(cipherB64),
      util.decodeBase64(nonceB64),
      util.decodeBase64(senderPubB64),
      mySecretU8
    );
    return opened ? util.encodeUTF8(opened) : null;
  } catch {
    return null;
  }
}

// ── seal a plaintext to EVERY device of a set of users (incl. my own) ──
//
// 🟥 TWO WAYS TO SEND A MESSAGE NOBODY CAN READ, and this used to allow both:
//
//   1. Sealing while OUR OWN public key isn't published. Every reader resolves the
//      sender's key to decrypt, so an unpublished sender key locks the message on every
//      device at once — the recipient's phone AND our own. That's why the publish is
//      verified here, before anything is sealed, rather than once at startup where a
//      single failed write is never noticed and never retried.
//
//   2. Treating a FAILED device-key read as "that user has no devices". getDeviceKeys
//      used to swallow the error and return {}, the spread quietly dropped that person,
//      and the message went out addressed to nobody but us. The phone refuses to ship in
//      this situation rather than send something unreadable; so does this now.
export async function sealForUsers(text, userIds, opts) {
  const me = await getIdentity();

  // cheap and TTL-cached — one extra read every few minutes, not one per message
  const published = await ensureKeyPublished();
  if (!published) {
    throw new Error("This browser isn't fully linked yet — reload the page and try again.");
  }

  const perUser = [];
  for (const uid of userIds || []) {
    if (!uid) continue;
    // eslint-disable-next-line no-await-in-loop
    const keys = await sealKeysFor(uid, me.deviceId); // fresh: pick up newly-paired devices (and drop gone ones)
    if (keys === null) {
      throw new Error("Couldn't reach the key directory — the message was not sent.");
    }
    // a user with a device entry but no publicKey on it is the phone's own known
    // failure mode (a push-token write creates the entry before the key lands) — worth
    // saying out loud, because that person alone will see a padlock
    if (!Object.keys(keys).length) {
      console.warn('[e2ee] no published device key for', uid, '\u2014 they will not be able to read this message');
    }
    perUser.push({ uid, devices: keys });
  }
  // 🟥 buildSealMap, NOT a spread — see its header. A spread here is the bug.
  const allDevices = buildSealMap(perUser);

  const perDeviceKeys = {};
  // 🔒 a LONG message text also seals a short preview into each copy ({pv, pn}) — its full
  //    cipher cannot fit a push (see ./textPreview.js); readers that don't know them ignore them
  const pv = opts && opts.preview && needsPreview(text) ? previewText(text) : null;
  for (const deviceId of Object.keys(allDevices)) {
    const sealed = encryptText(text, allDevices[deviceId], me.secretKeyU8);
    if (sealed) {
      perDeviceKeys[deviceId] = sealed;
      if (pv) {
        const p = encryptText(pv, allDevices[deviceId], me.secretKeyU8);
        if (p) { sealed.pv = p.text; sealed.pn = p.nonce; }
      }
    }
  }
  if (!Object.keys(perDeviceKeys).length) {
    throw new Error('Could not secure that message — it was not sent.');
  }
  return { senderDeviceId: me.deviceId, perDeviceKeys };
}


// ── decrypt an incoming per-device text message addressed to THIS browser ──
export async function decryptPerDeviceText(msg) {
  try {
    if (!msg?.perDeviceKeys || !msg?.senderDeviceId || !msg?.senderId) return null;
    const me = await getIdentity();
    // namespaced first, then plain — a colliding deviceId is sealed as `uid:deviceId`
    const myCopy = myCopyOf(msg.perDeviceKeys, me.deviceId);
    if (!myCopy) return null; // not sealed to this browser (e.g. pre-pairing history)

    // sender's public key for the exact device that sent it
    let senderKeys = (await getDeviceKeys(msg.senderId)) || {};
    let senderPub = senderKeys[msg.senderDeviceId];
    if (!senderPub) {
      senderKeys = (await getDeviceKeys(msg.senderId, true)) || {};
      senderPub = senderKeys[msg.senderDeviceId];
    }
    if (!senderPub) return null;

    let plain = decryptText(myCopy.text, myCopy.nonce, senderPub, me.secretKeyU8);
    if (!plain) {
      // stale sender key (they rotated) — one forced refresh + retry, like mobile
      const fresh = (await getDeviceKeys(msg.senderId, true)) || {};
      const freshPub = fresh[msg.senderDeviceId];
      if (freshPub && freshPub !== senderPub) {
        plain = decryptText(myCopy.text, myCopy.nonce, freshPub, me.secretKeyU8);
      }
    }
    return plain;
  } catch {
    return null;
  }
}

// ── unseal a per-device MEDIA key blob ({k,n}) so media/media.js can decrypt bytes ──
// Media messages seal the symmetric key JSON per-device in the SAME shape as text,
// under a media-specific field (imagePerDeviceKeys / batchPerDeviceKeys / …).
export async function unsealMediaKey(perDeviceKeys, senderId, senderDeviceId) {
  try {
    if (!perDeviceKeys || !senderDeviceId) return null;
    const me = await getIdentity();
    const myCopy = myCopyOf(perDeviceKeys, me.deviceId);
    if (!myCopy) return null;
    let senderKeys = (await getDeviceKeys(senderId)) || {};
    let senderPub = senderKeys[senderDeviceId];
    if (!senderPub) { senderKeys = (await getDeviceKeys(senderId, true)) || {}; senderPub = senderKeys[senderDeviceId]; }
    if (!senderPub) return null;
    const json = decryptText(myCopy.text, myCopy.nonce, senderPub, me.secretKeyU8);
    return json ? JSON.parse(json) : null; // { k, n, type? } or [{k,n,type}, …] for batches
  } catch {
    return null;
  }
}

// ── seal SEVERAL plaintexts to the same audience in ONE directory pass ──
//
// 🟢 A reply seals two things (the quote text and its thumbnail) to the same set of
//    devices. Calling sealForUsers twice re-reads every participant's device document
//    a second time — `getDeviceKeys(uid, true)` forces a fresh Firestore read, so on a
//    12-person group that's 24 reads for one message instead of 12. Same primitive,
//    same output shape; a null/empty entry yields a null map so callers can pass
//    optional payloads positionally.
export async function sealManyForUsers(texts, userIds) {
  const me = await getIdentity();
  const published = await ensureKeyPublished();
  if (!published) throw new Error("This browser isn't fully linked yet — reload the page and try again.");
  const perUser = [];
  for (const uid of userIds || []) {
    if (!uid) continue;
    // eslint-disable-next-line no-await-in-loop
    const keys = await sealKeysFor(uid, me.deviceId);
    if (keys === null) throw new Error("Couldn't reach the key directory.");
    perUser.push({ uid, devices: keys });
  }
  const allDevices = buildSealMap(perUser);   // same collision rule as sealForUsers
  const deviceIds = Object.keys(allDevices);
  const maps = (texts || []).map((text) => {
    if (text == null || text === '') return null;
    const map = {};
    for (const deviceId of deviceIds) {
      const sealed = encryptText(text, allDevices[deviceId], me.secretKeyU8);
      if (sealed) map[deviceId] = sealed;
    }
    return Object.keys(map).length ? map : null;
  });
  return { senderDeviceId: me.deviceId, maps };
}
